import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  testRunWorkflow, setWorkflowStatus, listExecutions, getExecution, cancelExecution,
} from 'features/agents/api/agentsApi';
import usePolling from 'shared/hooks/usePolling';

const FINISHED = ['succeeded', 'failed', 'cancelled'];

/**
 * The editor's test runs and everything that follows them.
 *
 * "Test" runs the draft without publishing and returns every step's result at
 * once. For an agent started by something else (a webhook, an event) the page
 * can instead wait for the next run: poll for a new execution from 2s backing
 * off to 15s (never while the tab is hidden), then follow that one until it
 * finishes. `stepByNodeId` is whichever of the two is current, and is what
 * the canvas colours its nodes from.
 *
 * @param {object} p
 * @param {string} p.workflowId
 * @param {boolean} p.isDraft            a /new draft has nothing to run yet
 * @param {object} p.definition
 * @param {() => Promise<void>} p.flushDraft  save pending edits first
 * @param {(msg: string) => void} p.setError
 * @param {() => Promise<void>} p.refreshWorkflow  reload the agent after pausing it
 */
export default function useRunPolling({ workflowId, isDraft, definition, flushDraft, setError, refreshWorkflow }) {
  const [runningManual, setRunningManual] = useState(false);
  const [draftTestSteps, setDraftTestSteps] = useState(null);
  const [liveExecId, setLiveExecId] = useState(null);
  const [liveExec, setLiveExec] = useState(null);
  const [awaitingTrigger, setAwaitingTrigger] = useState(false);
  const [activatedNonManual, setActivatedNonManual] = useState(false);
  const [testData, setTestData] = useState({});
  const execBaselineRef = useRef(new Set());

  const testActive = runningManual || awaitingTrigger
    || (!!liveExecId && (!liveExec || liveExec.status === 'running' || liveExec.status === 'queued'));
  const testVisible = testActive || !!draftTestSteps || (!!liveExec && FINISHED.includes(liveExec.status));

  const clear = useCallback(() => {
    setDraftTestSteps(null); setLiveExecId(null); setLiveExec(null); setAwaitingTrigger(false); setActivatedNonManual(false);
  }, []);

  const handleRunNow = useCallback(async () => {
    if (isDraft) { setError('Save the agent to test it.'); return; }
    setRunningManual(true);
    clear();
    try {
      await flushDraft();
      const nodesOut = await testRunWorkflow(workflowId, { definition });
      setTestData((prev) => ({ ...prev, ...nodesOut }));
      const steps = {};
      for (const [nodeId, step] of Object.entries(nodesOut)) steps[nodeId] = { node_id: nodeId, ...step };
      setDraftTestSteps(steps);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Test failed');
    } finally { setRunningManual(false); }
  }, [isDraft, workflowId, definition, flushDraft, setError, clear]);

  usePolling(async (isActive) => {
    try {
      const list = await listExecutions(workflowId);
      if (!isActive()) return false;
      const fresh = (list || []).filter((e) => !execBaselineRef.current.has(e.id));
      if (fresh.length) { setLiveExecId(fresh[0].id); setAwaitingTrigger(false); return false; }
    } catch (_) { /* keep polling */ }
    return true;
  }, { enabled: awaitingTrigger && !liveExecId, interval: 2000, maxInterval: 15000, key: workflowId });

  usePolling(async (isActive) => {
    try {
      const detail = await getExecution(liveExecId);
      if (!isActive()) return false;
      setLiveExec(detail);
      if (detail && detail.status !== 'running' && detail.status !== 'queued') return false;
    } catch (_) { /* keep polling */ }
    return true;
  }, { enabled: Boolean(liveExecId), interval: 1500, maxInterval: 10000, key: liveExecId });

  const stopTest = useCallback(async () => {
    try {
      if (liveExec && (liveExec.status === 'running' || liveExec.status === 'queued')) {
        try { await cancelExecution(liveExec.id); } catch (_) { /* already over */ }
      }
      if (activatedNonManual) {
        try { await setWorkflowStatus(workflowId, 'paused'); } catch (_) { /* best effort */ }
        await refreshWorkflow();
      }
    } finally {
      clear();
    }
  }, [liveExec, activatedNonManual, workflowId, refreshWorkflow, clear]);

  const stepByNodeId = useMemo(() => {
    if (draftTestSteps) return draftTestSteps;
    const out = {};
    for (const s of (liveExec?.steps || [])) out[s.node_id] = s;
    return out;
  }, [liveExec, draftTestSteps]);

  // One step's test, from its settings panel (Test step / load its input).
  const runTest = useCallback(async (untilNodeId, options = {}) => {
    if (isDraft) throw new Error('Save the agent to test its steps.');
    const nodesOut = await testRunWorkflow(workflowId, { definition, untilNodeId, inputsOnly: !!options.inputsOnly });
    setTestData((prev) => {
      const next = { ...prev, ...nodesOut };
      if (options.inputsOnly) delete next[untilNodeId];
      return next;
    });
    return nodesOut;
  }, [isDraft, workflowId, definition]);

  // A different agent starts with a clean slate.
  useEffect(() => { clear(); setTestData({}); }, [workflowId, clear]);

  return {
    runningManual, testActive, testVisible, awaitingTrigger, stepByNodeId, testData,
    handleRunNow, stopTest, runTest,
  };
}
